// Drill-down sloupce (Miller columns) — skupina otevře nový sloupec vpravo,
// uživatel vidí celou cestu. Jediný způsob procházení menu.

import { Fragment, useState } from 'react';

import { cn } from '@/shared/lib/utils';

import { LoadFooter } from '../LoadIndicator';
import { MenuRow } from '../MenuRow';
import { SectionSeparator } from '../SectionSeparator';
import { useMenuConfig } from '../menuConfigContext';
import { splitSections } from '../menuTreeUtils';
import type { MenuGroupNode, MenuLoadViz, MenuNode, MenuSection } from '../menuTypes';

export interface ColumnsViewProps {
  readonly section: MenuSection;
  readonly loadViz: MenuLoadViz;
  readonly ready: boolean;
}

export function ColumnsView({ section, loadViz, ready }: ColumnsViewProps) {
  const { densityTokens: d } = useMenuConfig();
  const [colPath, setColPath] = useState<MenuGroupNode[]>([]);

  const cols = [
    { items: section.items, label: section.label },
    ...colPath.map((g) => ({ items: g.items, label: g.title })),
  ];
  const selectCol = (ci: number, node: MenuGroupNode) =>
    setColPath((cp) => cp.slice(0, ci).concat(node));

  return (
    <div className="flex items-stretch">
      {cols.map((col, ci) => (
        <div
          key={ci}
          className={cn(
            'border-phx-border flex grow flex-col border-r last:border-r-0',
            d.colBasis,
            d.colMinW,
            'max-w-[360px]'
          )}
        >
          {ci > 0 && (
            <div className="text-phx-text-muted pb-phx-2_5 text-phx-2xs truncate px-2.5 pt-2 font-bold tracking-wider uppercase">
              {col.label}
            </div>
          )}
          <div className="phx-menu-scroll max-h-[min(72vh,580px)] overflow-y-auto p-1">
            <ColumnList items={col.items} ci={ci} colPath={colPath} selectCol={selectCol} />
            {ci === 0 && <LoadFooter viz={loadViz} ready={ready} />}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ColumnListProps {
  readonly items: readonly MenuNode[];
  readonly ci: number;
  readonly colPath: readonly MenuGroupNode[];
  readonly selectCol: (ci: number, node: MenuGroupNode) => void;
}

function ColumnList({ items, ci, colPath, selectCol }: ColumnListProps) {
  const { separators, onOpen } = useMenuConfig();
  const sections = splitSections(items);
  return (
    <>
      {sections.map((section, si) => (
        <Fragment key={si}>
          {si > 0 && (
            <SectionSeparator
              sep={section.sep === 'none' ? 'space' : section.sep}
              variant={separators}
            />
          )}
          {section.nodes.map((node) => {
            if (node.t === 'item') {
              return (
                <MenuRow
                  key={node.stableId ?? node.id}
                  node={node}
                  onActivate={() => onOpen(node)}
                />
              );
            }
            if (node.t !== 'group') return null;
            const selected = colPath[ci] === node;
            return (
              <MenuRow
                key={node.stableId ?? node.id}
                node={node}
                selected={selected}
                onActivate={() => selectCol(ci, node)}
              />
            );
          })}
        </Fragment>
      ))}
    </>
  );
}
